import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ImagePickerModal from "../../../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../../../hooks/app/useImagePickerModal";
import {
  fetchCreationImageBlob,
  fetchExportImageBlob,
  fetchUploadImageBlob,
} from "../../../../services/api/editor/background_removal";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../../../utils/openLibraryInEditor";
import {
  applyWatermarkJobPreset,
  getWatermarkJobPreset,
  getWatermarkPresetBadge,
} from "../../../../config/tools/watermarkPresets";
import {
  WATERMARK_CONTENT_TYPES,
  WATERMARK_SYMBOLS,
  WATERMARK_TEXT_CASES,
  applyMarginAlongNearestEdge,
  applyWatermarkContentType,
  applyWatermarkTextCase,
  applyWatermarkTilePreset,
  getWatermarkFontStack,
  getWatermarkFontsForType,
  markEdgeGapsPx,
  marginFromNearestEdgeGap,
  measureWatermarkText,
  resolveMarginGapPx,
  resolveSingleMarkPosition,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
} from "./watermarkSettings";
import WatermarkPresetMenu from "./WatermarkPresetMenu";
import WatermarkColorMenu from "./WatermarkColorMenu";
import WatermarkFieldSlider from "./WatermarkFieldSlider";
import CoolCheckbox from "../../../../components/shared/CoolCheckbox";
import ToolPrepUploadDropzone from "../../shared/Popup/ToolPrepUploadDropzone";
import TextFormatPositionPanel from "../../../../components/shared/textToolbar/TextFormatPositionPanel";
import {
  textFormatPatchToWatermark,
  watermarkToTextFormat,
} from "../Editor/watermarkTextFormatAdapter";
import { runWithMinUploadProgress } from "../../../../utils/runWithMinUploadProgress";
import "./WatermarkControls.css";

const TileSingleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="9" cy="9" r="2.5" fill="currentColor" />
  </svg>
);

const TileGridIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="5.5" cy="5.5" r="1.8" fill="currentColor" />
    <circle cx="12.5" cy="5.5" r="1.8" fill="currentColor" />
    <circle cx="5.5" cy="12.5" r="1.8" fill="currentColor" />
    <circle cx="12.5" cy="12.5" r="1.8" fill="currentColor" />
  </svg>
);

const TileDiamondIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="9" cy="4" r="1.7" fill="currentColor" />
    <circle cx="4.5" cy="9" r="1.7" fill="currentColor" />
    <circle cx="13.5" cy="9" r="1.7" fill="currentColor" />
    <circle cx="9" cy="14" r="1.7" fill="currentColor" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.25" />
    <path
      d="M7 6.2V10M7 4.2v.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
  </svg>
);

function fileFromBlob(blob, filename = "logo.png") {
  if (blob instanceof File) return blob;
  const type = blob?.type || "image/png";
  const name = String(filename || "logo.png").replace(/[^\w.\-]+/g, "_");
  return new File([blob], name || "logo.png", { type });
}

const CLEARED_LOGO_SOURCE = {
  logoFile: null,
  logoCreationId: null,
  logoRootCreationId: null,
  logoOriginalUrl: null,
  logoBgRemoved: false,
};

/**
 * Watermark Maker left-panel controls (LazyStudio dark theme).
 */
const WatermarkControls = ({
  settings,
  onChange,
  disabled = false,
  /** When true, universal format controls live on the canvas toolbar (editor). */
  hideFormatControls = false,
  /** Image watermark export bake toggle. Hidden for PDF/Word (always baked on download). */
  showEmbedToggle = true,
  colorPickerAnchorSelector = '.tool-prep-popup[data-tool="watermark_maker"] .tool-prep-popup__left',
  presetMenuAnchorSelector = '.tool-prep-popup[data-tool="watermark_maker"] .tool-prep-popup__left',
  presetMenuAnchorTopSelector = '.tool-prep-popup[data-tool="watermark_maker"] .tool-prep-popup__left',
  positionStageSelector = ".transform-preview__canvas",
}) => {
  const [fontOpen, setFontOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const fontRef = useRef(null);
  const textInputRef = useRef(null);
  const embedHintId = useId();
  const tileHintId = useId();
  const isSingleTile = (settings.tile || "single") === "single";
  const { openImagePicker, imagePickerProps: logoPickerProps } =
    useImagePickerModal();
  const { getToken } = useAuth();
  const logoObjectUrlRef = useRef(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [logoUploadProgress, setLogoUploadProgress] = useState(null);

  const selectedPreset = settings.presetId
    ? getWatermarkJobPreset(settings.presetId)
    : null;

  const contentType =
    settings.contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
      ? WATERMARK_CONTENT_TYPES.SIGNATURE
      : settings.contentType === WATERMARK_CONTENT_TYPES.LOGO
        ? WATERMARK_CONTENT_TYPES.LOGO
        : WATERMARK_CONTENT_TYPES.TEXT;
  const isLogo = contentType === WATERMARK_CONTENT_TYPES.LOGO;

  const fontOptions = useMemo(
    () => getWatermarkFontsForType(contentType),
    [contentType]
  );
  const positionValue = useMemo(() => watermarkToTextFormat(settings), [settings]);
  const font =
    fontOptions.find((f) => f.id === settings.fontId) || fontOptions[0];

  useEffect(() => {
    if (!fontOpen) return undefined;
    const onDoc = (e) => {
      if (fontOpen && fontRef.current && !fontRef.current.contains(e.target)) {
        setFontOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [fontOpen]);

  const patch = (partial) =>
    onChange({ ...settingsRef.current, ...partial });

  const readStageSize = () => {
    const el = positionStageSelector
      ? document.querySelector(positionStageSelector)
      : null;
    return {
      width: el?.offsetWidth || 400,
      height: el?.offsetHeight || 400,
    };
  };

  const resolveMarkMetrics = (w, h) => {
    const sizeInternal = Number(settings.size) || 0;
    if (isLogo) {
      return watermarkLogoDisplaySize(
        sizeInternal,
        w,
        h,
        Number(settings.logoNaturalWidth) || 0,
        Number(settings.logoNaturalHeight) || 0
      );
    }
    return measureWatermarkText(
      settings.text || "Your text",
      watermarkFontSizePx(sizeInternal, w, h),
      getWatermarkFontStack(settings.fontId),
      settings.fontWeight,
      {
        letterSpacing: settings.letterSpacing,
        textScript: settings.textScript,
      }
    );
  };

  const nearestMarginPct = useMemo(() => {
    if (!isSingleTile) {
      return Math.round((Number(settings.margin) || 0) * 100);
    }
    const el = positionStageSelector
      ? document.querySelector(positionStageSelector)
      : null;
    const w = el?.offsetWidth || 400;
    const h = el?.offsetHeight || 400;
    const metrics = (() => {
      const sizeInternal = Number(settings.size) || 0;
      if (isLogo) {
        return watermarkLogoDisplaySize(
          sizeInternal,
          w,
          h,
          Number(settings.logoNaturalWidth) || 0,
          Number(settings.logoNaturalHeight) || 0
        );
      }
      return measureWatermarkText(
        settings.text || "Your text",
        watermarkFontSizePx(sizeInternal, w, h),
        getWatermarkFontStack(settings.fontId),
        settings.fontWeight,
        {
          letterSpacing: settings.letterSpacing,
          textScript: settings.textScript,
        }
      );
    })();
    const base = resolveSingleMarkPosition({
      position: settings.position || "center",
      margin: settings.margin,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(settings.rotation) || 0,
      flushEdges: settings.flushEdges !== false,
    });
    const gaps = markEdgeGapsPx({
      centerX: base.x + (Number(settings.offsetX) || 0),
      centerY: base.y + (Number(settings.offsetY) || 0),
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(settings.rotation) || 0,
    });
    const gapPx = resolveMarginGapPx(gaps, settings.position || "center");
    return Math.round(marginFromNearestEdgeGap(gapPx, w, h) * 100);
  }, [isSingleTile, isLogo, settings, positionStageSelector]);

  const selectTile = (tile) => {
    onChange(applyWatermarkTilePreset(settings, tile));
  };

  const handlePositionChange = (next) => {
    const base = settingsRef.current;
    const { _meta, textCase: _textCase, text: _text, ...incoming } = next;
    const current = watermarkToTextFormat(base);
    const positionPatch = {};
    for (const [key, val] of Object.entries(incoming)) {
      if (current[key] !== val) positionPatch[key] = val;
    }
    if (Object.keys(positionPatch).length === 0) return;
    onChange(textFormatPatchToWatermark(positionPatch, base));
  };

  const selectContentType = (nextType) => {
    setFontOpen(false);
    setColorOpen(false);
    setPresetOpen(false);
    onChange(applyWatermarkContentType(settingsRef.current, nextType));
  };

  const selectJobPreset = (preset) => {
    setFontOpen(false);
    setColorOpen(false);
    onChange(applyWatermarkJobPreset(settingsRef.current, preset));
  };

  const revokeLogoUrl = () => {
    if (logoObjectUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
    }
    logoObjectUrlRef.current = null;
  };

  const applyLogoFromUrl = useCallback(
    (url, source = {}) => {
      if (!url) return;
      const apply = (nw, nh) => {
        onChange({
          ...settingsRef.current,
          logoUrl: url,
          logoOriginalUrl: url,
          logoNaturalWidth: nw,
          logoNaturalHeight: nh,
          logoBgRemoved: false,
          logoFile: source.logoFile instanceof File ? source.logoFile : null,
          logoCreationId: source.logoCreationId ?? null,
          logoRootCreationId: source.logoRootCreationId ?? null,
          logoCropAspect: "custom",
          logoCropRect: null,
          logoCropFrame: null,
          logoCropImageFrame: null,
          logoCropActive: false,
          logoCropSessionStartImageFrame: null,
          logoCropInset: 0,
        });
      };
      const img = new Image();
      if (
        typeof url === "string" &&
        /^https?:\/\//i.test(url) &&
        !url.startsWith("blob:")
      ) {
        img.crossOrigin = "anonymous";
      }
      img.onload = () => {
        apply(img.naturalWidth || img.width || 0, img.naturalHeight || img.height || 0);
      };
      img.onerror = () => apply(0, 0);
      img.src = url;
    },
    [onChange]
  );

  const applyLibraryLogo = useCallback(
    async (item) => {
      if (!item) return;
      const exportId = libraryItemExportId(item);
      const creationId = libraryItemCreationId(item);
      const rootCreationId =
        item.kind === "upload"
          ? item.root_creation_id ?? item.creation_id ?? creationId
          : item.root_creation_id ?? null;
      const source = {
        logoCreationId: creationId,
        logoRootCreationId: rootCreationId,
      };

      try {
        let blob = null;
        let outputUrlFallback = null;

        await runWithMinUploadProgress({
          setProgress: setLogoUploadProgress,
          minMs: 1400,
          work: async () => {
            const token = await getToken();
            if (!token) throw new Error("Sign in required.");

            if (item.kind === "upload") {
              const uploadId = source.logoRootCreationId ?? creationId;
              if (!uploadId) throw new Error("Could not resolve library image.");
              blob = await fetchUploadImageBlob(token, uploadId);
            } else if (item.kind === "export" || exportId) {
              blob = await fetchExportImageBlob(token, exportId);
            } else if (creationId) {
              blob = await fetchCreationImageBlob(token, creationId);
            } else if (item.output_url) {
              outputUrlFallback = item.output_url;
              return;
            } else {
              throw new Error("Could not resolve library image.");
            }

            if (!blob || blob.type?.includes("json")) {
              throw new Error("Could not load library image.");
            }
          },
          onReady: () => {
            if (outputUrlFallback) {
              revokeLogoUrl();
              logoObjectUrlRef.current = null;
              applyLogoFromUrl(outputUrlFallback, source);
              return;
            }
            const file = fileFromBlob(
              blob,
              item.file_name || item.name || "logo.png"
            );
            revokeLogoUrl();
            const url = URL.createObjectURL(file);
            logoObjectUrlRef.current = url;
            applyLogoFromUrl(url, { ...source, logoFile: file });
          },
        });
      } catch (err) {
        console.error("[WatermarkControls] library logo pick failed:", err);
        setLogoUploadProgress(null);
        if (item.output_url && !logoObjectUrlRef.current) {
          revokeLogoUrl();
          applyLogoFromUrl(item.output_url, source);
        }
      }
    },
    [applyLogoFromUrl, getToken]
  );

  const openLogoPicker = () => {
    if (disabled || logoUploadProgress != null) return;
    openImagePicker({
      onPickFile: async (file) => {
        if (!file) return;
        try {
          await runWithMinUploadProgress({
            setProgress: setLogoUploadProgress,
            onReady: () => {
              revokeLogoUrl();
              const url = URL.createObjectURL(file);
              logoObjectUrlRef.current = url;
              applyLogoFromUrl(url, { logoFile: file });
            },
          });
        } catch (err) {
          console.error("[WatermarkControls] logo pick failed:", err);
          setLogoUploadProgress(null);
        }
      },
      onPickLibrary: applyLibraryLogo,
    });
  };

  const clearLogo = () => {
    if (disabled) return;
    revokeLogoUrl();
    patch({
      logoUrl: null,
      logoNaturalWidth: 0,
      logoNaturalHeight: 0,
      ...CLEARED_LOGO_SOURCE,
      logoCropAspect: "custom",
      logoCropRect: null,
      logoCropFrame: null,
      logoCropImageFrame: null,
      logoCropActive: false,
      logoCropSessionStartImageFrame: null,
      logoCropInset: 0,
    });
  };

  const insertSymbol = (char) => {
    if (disabled) return;
    const input = textInputRef.current;
    const current = String(settings.text ?? "");
    if (input && typeof input.selectionStart === "number") {
      const start = input.selectionStart;
      const end = input.selectionEnd ?? start;
      const next = current.slice(0, start) + char + current.slice(end);
      patch({ text: next });
      requestAnimationFrame(() => {
        const pos = start + char.length;
        input.focus();
        input.setSelectionRange(pos, pos);
      });
      return;
    }
    patch({ text: `${current}${char}` });
  };

  return (
    <>
    <ImagePickerModal {...logoPickerProps} />
    <WatermarkPresetMenu
      isOpen={presetOpen}
      onClose={() => setPresetOpen(false)}
      selectedId={settings.presetId || null}
      onSelect={selectJobPreset}
      anchorSelector={presetMenuAnchorSelector}
      anchorTopSelector={presetMenuAnchorTopSelector}
      anchorGap={10}
      ignoreCloseSelectors={[
        "#wmc-preset-trigger",
        "#wmc-preset-section",
        ".wmc-preset-menu",
      ]}
    />
    <WatermarkColorMenu
      isOpen={colorOpen}
      onClose={() => setColorOpen(false)}
      color={settings.color}
      onChange={(c) => patch({ color: c })}
      onApply={(c) => patch({ color: c })}
      anchorSelector={colorPickerAnchorSelector}
      anchorGap={10}
      ignoreCloseSelectors={[
        "#wmc-color-trigger",
        ".wmc-select--color",
        ".wmc-field--color",
        ".wmc-color-menu",
      ]}
    />
    <div className={`wmc${disabled ? " is-disabled" : ""}`}>
      <div className="wmc-field" id="wmc-preset-section">
        <span className="wmc-field__label">Presets</span>
        <button
          type="button"
          id="wmc-preset-trigger"
          className={`wmc-select wmc-select--preset${
            presetOpen ? " is-open" : ""
          }`}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={presetOpen}
          onClick={() => {
            setFontOpen(false);
            setColorOpen(false);
            setPresetOpen((v) => !v);
          }}
        >
          <span className="wmc-select__preview wmc-select__preview--preset" aria-hidden="true">
            ✦
          </span>
          <span className="wmc-select__divider" aria-hidden="true" />
          <span className="wmc-select__label">
            {selectedPreset?.title || "Choose a preset"}
          </span>
          {selectedPreset ? (
            <span className="wmc-select__meta">
              {getWatermarkPresetBadge(selectedPreset)}
            </span>
          ) : null}
          <ChevronIcon />
        </button>
      </div>

      <div className="wmc-field">
        <span className="wmc-field__label">Type</span>
        <div className="wmc-mode-row" role="group" aria-label="Watermark type">
          <button
            type="button"
            className={`wmc-mode${
              contentType === WATERMARK_CONTENT_TYPES.TEXT ? " is-active" : ""
            }`}
            disabled={disabled}
            aria-pressed={contentType === WATERMARK_CONTENT_TYPES.TEXT}
            onClick={() => selectContentType(WATERMARK_CONTENT_TYPES.TEXT)}
          >
            Text
          </button>
          <button
            type="button"
            className={`wmc-mode${isLogo ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={isLogo}
            onClick={() => selectContentType(WATERMARK_CONTENT_TYPES.LOGO)}
          >
            Logo
          </button>
          <button
            type="button"
            className={`wmc-mode${
              contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
                ? " is-active"
                : ""
            }`}
            disabled={disabled}
            aria-pressed={contentType === WATERMARK_CONTENT_TYPES.SIGNATURE}
            onClick={() =>
              selectContentType(WATERMARK_CONTENT_TYPES.SIGNATURE)
            }
          >
            Signature
          </button>
        </div>
      </div>

      {isLogo ? (
        <>
        <div className="wmc-field">
          <span className="wmc-field__label">Logo</span>
          {logoUploadProgress != null ? (
            <ToolPrepUploadDropzone
              kind="logo"
              progress={logoUploadProgress}
            />
          ) : settings.logoUrl ? (
            <div className="wmc-logo-preview">
              <img src={settings.logoUrl} alt="Logo watermark" />
              <div className="wmc-logo-preview__actions">
                <button
                  type="button"
                  className="wmc-logo-btn"
                  disabled={disabled}
                  onClick={openLogoPicker}
                >
                  Replace
                </button>
                <button
                  type="button"
                  className="wmc-logo-btn wmc-logo-btn--danger"
                  disabled={disabled}
                  onClick={clearLogo}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <ToolPrepUploadDropzone
              kind="logo"
              onClick={openLogoPicker}
              disabled={disabled}
            />
          )}
        </div>

      <div className="wmc-field">
        <span className="wmc-field__label-row">
          <span className="wmc-field__label">Tile</span>
          <span
            className="wmc-field__hint"
            title="Single places one mark (use Position). Grid and Tile repeat across the image."
            aria-describedby={`${tileHintId}-logo`}
          >
            <InfoIcon />
          </span>
          <span id={`${tileHintId}-logo`} className="wmc-sr-only">
            Single places one mark and uses Position. Grid and Tile repeat across
            the image.
          </span>
        </span>
        <div className="wmc-tile-row" role="group" aria-label="Tile">
          <button
            type="button"
            className={`wmc-tile${isSingleTile ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={isSingleTile}
            title="Single"
            onClick={() => selectTile("single")}
          >
            <TileSingleIcon />
          </button>
          <button
            type="button"
            className={`wmc-tile${settings.tile === "grid" ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={settings.tile === "grid"}
            title="Grid"
            onClick={() => selectTile("grid")}
          >
            <TileGridIcon />
          </button>
          <button
            type="button"
            className={`wmc-tile${settings.tile === "diamond" ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={settings.tile === "diamond"}
            title="Tile"
            onClick={() => selectTile("diamond")}
          >
            <TileDiamondIcon />
          </button>
        </div>
      </div>

        {!hideFormatControls ? (
        <WatermarkFieldSlider
          label="Size"
          value={settings.size}
          min={0}
          max={24}
          step={0.01}
          suffix="x"
          disabled={disabled}
          onChange={(size) => patch({ size })}
        />
        ) : null}
        </>
      ) : (
        <>
      <div className="wmc-field">
        <label className="wmc-field__label" htmlFor="wmc-text">
          {contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
            ? "Signature text"
            : "Text"}
        </label>
        <input
          id="wmc-text"
          ref={textInputRef}
          type="text"
          className="wmc-input"
          value={settings.text}
          disabled={disabled}
          placeholder={
            contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
              ? "Your signature"
              : "Your Text"
          }
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => patch({ text: e.target.value })}
        />
        {!hideFormatControls ? (
        <div
          className="wmc-mode-row wmc-case-row"
          role="group"
          aria-label="Text case"
        >
          {WATERMARK_TEXT_CASES.map((c) => (
            <button
              key={c.id}
              type="button"
              className="wmc-mode wmc-case"
              disabled={disabled || !String(settings.text || "").trim()}
              title={c.title || c.label}
              aria-label={c.title || c.label}
              onClick={() =>
                patch({ text: applyWatermarkTextCase(settings.text, c.id) })
              }
            >
              {c.label}
            </button>
          ))}
        </div>
        ) : null}
        <div className="wmc-symbol-row" role="group" aria-label="Insert symbol">
          {WATERMARK_SYMBOLS.map((sym) => (
            <button
              key={sym.id}
              type="button"
              className="wmc-symbol"
              disabled={disabled}
              title={`Insert ${sym.label}`}
              onClick={() => insertSymbol(sym.char)}
            >
              {sym.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wmc-field">
        <span className="wmc-field__label-row">
          <span className="wmc-field__label">Tile</span>
          <span
            className="wmc-field__hint"
            title="Single places one mark (use Position). Grid and Tile repeat across the image."
            aria-describedby={tileHintId}
          >
            <InfoIcon />
          </span>
          <span id={tileHintId} className="wmc-sr-only">
            Single places one mark and uses Position. Grid and Tile repeat across
            the image.
          </span>
        </span>
        <div className="wmc-tile-row" role="group" aria-label="Tile">
          <button
            type="button"
            className={`wmc-tile${isSingleTile ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={isSingleTile}
            title="Single"
            onClick={() => selectTile("single")}
          >
            <TileSingleIcon />
          </button>
          <button
            type="button"
            className={`wmc-tile${settings.tile === "grid" ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={settings.tile === "grid"}
            title="Grid"
            onClick={() => selectTile("grid")}
          >
            <TileGridIcon />
          </button>
          <button
            type="button"
            className={`wmc-tile${settings.tile === "diamond" ? " is-active" : ""}`}
            disabled={disabled}
            aria-pressed={settings.tile === "diamond"}
            title="Tile"
            onClick={() => selectTile("diamond")}
          >
            <TileDiamondIcon />
          </button>
        </div>
      </div>

      {!hideFormatControls ? (
      <div className="wmc-field" ref={fontRef}>
        <span className="wmc-field__label">
          {contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
            ? "Signature font"
            : "Font"}
        </span>
        <button
          type="button"
          className="wmc-select"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={fontOpen}
          onClick={() => {
            setColorOpen(false);
            setPresetOpen(false);
            setFontOpen((v) => !v);
          }}
        >
          <span
            className="wmc-select__preview"
            style={{ fontFamily: getWatermarkFontStack(font.id) }}
            aria-hidden="true"
          >
            Ag
          </span>
          <span className="wmc-select__divider" aria-hidden="true" />
          <span className="wmc-select__label">{font.label}</span>
          <ChevronIcon />
        </button>
        {fontOpen ? (
          <ul className="wmc-menu" role="listbox" aria-label="Font">
            {fontOptions.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={f.id === font.id}
                  className={`wmc-menu__item${f.id === font.id ? " is-active" : ""}`}
                  style={{ fontFamily: f.stack }}
                  onClick={() => {
                    patch({ fontId: f.id });
                    setFontOpen(false);
                  }}
                >
                  {f.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      ) : null}

      {!hideFormatControls ? (
      <WatermarkFieldSlider
        label="Size"
        value={settings.size}
        min={0}
        max={24}
        step={0.01}
        suffix="x"
        disabled={disabled}
        onChange={(size) => patch({ size })}
      />
      ) : null}

      {!hideFormatControls ? (
      <div className="wmc-field">
        <span className="wmc-field__label">Thickness</span>
        <div className="wmc-mode-row" role="group" aria-label="Font thickness">
          <button
            type="button"
            className={`wmc-mode${
              (settings.fontWeight || "normal") === "normal" ? " is-active" : ""
            }`}
            disabled={disabled}
            aria-pressed={(settings.fontWeight || "normal") === "normal"}
            onClick={() => patch({ fontWeight: "normal" })}
          >
            Regular
          </button>
          <button
            type="button"
            className={`wmc-mode${
              settings.fontWeight === "bold" ? " is-active" : ""
            }`}
            disabled={disabled}
            aria-pressed={settings.fontWeight === "bold"}
            onClick={() => patch({ fontWeight: "bold" })}
          >
            Bold
          </button>
        </div>
      </div>
      ) : null}
        </>
      )}

      {!isLogo && !hideFormatControls ? (
      <div className="wmc-field wmc-field--color">
        <span className="wmc-field__label">Color</span>
        <button
          type="button"
          id="wmc-color-trigger"
          className={`wmc-select wmc-select--color${
            colorOpen ? " is-open" : ""
          }`}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={colorOpen}
          onClick={() => {
            setFontOpen(false);
            setPresetOpen(false);
            setColorOpen((v) => !v);
          }}
        >
          <span
            className="wmc-color-swatch"
            style={{ background: settings.color }}
            aria-hidden="true"
          />
          <span className="wmc-select__label">
            {settings.color.toUpperCase()}
          </span>
          <ChevronIcon />
        </button>
      </div>
      ) : null}

      {isSingleTile && !hideFormatControls ? (
      <div className="wmc-field wmc-field--position">
        <span className="wmc-field__label">Position</span>
        <TextFormatPositionPanel
          className="tfp-panel--inline"
          value={positionValue}
          onChange={handlePositionChange}
          disabled={disabled}
          stageSelector={positionStageSelector}
          sizeConfig={{
            formatDisplay: (v) => String(Math.round(v * 10)),
            parseDisplay: (display) => Number(display) / 10,
          }}
        />
      </div>
      ) : !isSingleTile && !isLogo && !hideFormatControls ? (
        <WatermarkFieldSlider
          label="Spacing"
          value={settings.spacing}
          min={0}
          max={3.96}
          step={0.01}
          suffix="x"
          disabled={disabled}
          onChange={(spacing) => patch({ spacing })}
        />
      ) : null}

      {!(hideFormatControls && isSingleTile) &&
      !(hideFormatControls && !isSingleTile && !isLogo) &&
      !(isLogo && !isSingleTile) ? (
      <WatermarkFieldSlider
        label="Margin"
        value={nearestMarginPct}
        min={0}
        max={40}
        step={1}
        suffix="%"
        disabled={disabled}
        onChange={(pct) => {
          if (!isSingleTile) {
            patch({
              margin: Math.max(0, Math.min(0.4, pct / 100)),
              offsetX: 0,
              offsetY: 0,
            });
            return;
          }
          const { width: w, height: h } = readStageSize();
          const metrics = resolveMarkMetrics(w, h);
          const live = settingsRef.current;
          const base = resolveSingleMarkPosition({
            position: live.position || "center",
            margin: live.margin,
            stageWidth: w,
            stageHeight: h,
            textWidth: metrics.width,
            textHeight: metrics.height,
            rotationDeg: Number(live.rotation) || 0,
            flushEdges: live.flushEdges !== false,
          });
          const applied = applyMarginAlongNearestEdge({
            centerX: base.x + (Number(live.offsetX) || 0),
            centerY: base.y + (Number(live.offsetY) || 0),
            position: live.position || "center",
            nextMargin: Math.max(0, Math.min(0.4, pct / 100)),
            stageWidth: w,
            stageHeight: h,
            textWidth: metrics.width,
            textHeight: metrics.height,
            rotationDeg: Number(live.rotation) || 0,
            flushEdges: live.flushEdges !== false,
          });
          patch({
            margin: applied.margin,
            offsetX: applied.offsetX,
            offsetY: applied.offsetY,
          });
        }}
      />
      ) : null}

      {!hideFormatControls ? (
      <WatermarkFieldSlider
        label="Opacity"
        value={settings.opacity}
        min={0}
        max={100}
        step={1}
        suffix="%"
        disabled={disabled}
        onChange={(opacity) => patch({ opacity })}
      />
      ) : null}

      {!isSingleTile && !isLogo && !hideFormatControls ? (
      <WatermarkFieldSlider
        label="Rotation"
        value={settings.rotation}
        min={-180}
        max={180}
        step={1}
        suffix="°"
        disabled={disabled}
        onChange={(rotation) => patch({ rotation })}
      />
      ) : null}

      {showEmbedToggle ? (
        <CoolCheckbox
          className="wmc-embed"
          checked={Boolean(settings.embed)}
          disabled={disabled}
          label="Embed watermark"
          onChange={(e) => patch({ embed: e.target.checked })}
        >
          <span
            className="wmc-embed__info"
            title="When enabled, the watermark is baked into the image on export."
            aria-describedby={embedHintId}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <InfoIcon />
          </span>
          <span id={embedHintId} className="wmc-sr-only">
            When enabled, the watermark is baked into the image on export.
          </span>
        </CoolCheckbox>
      ) : null}
    </div>
    </>
  );
};

export default WatermarkControls;
